<script setup>
import ToastComponent from '@/components/ToastComponent.vue';
import { useAxios } from '@/hooks/useAxios';
import { useCommonCodeStore } from '@/store/commoncode-store';
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue';

const props = defineProps({
    contractType: {
        type: String,
        default: 'INSU'
    },
    validMsg: {
        type: Object,
        default: () => ({})
    },
    requiredList: {
        type: [Array, Object],
        default: () => []
    },
    readonlyList: {
        type: [Array, Object],
        default: () => []
    },
    hideList: {
        type: [Array, Object],
        default: () => []
    },
    customerData: {
        type: Object,
        default: () => ({})
    }
});

const emit = defineEmits(['update']);
const toastComponentRef = ref(null);
const commonCodes = computed(() => useCommonCodeStore().getCommonCodes);
const commonCode = (code) => {
    const returnVal = computed(() => {
        return commonCodes.value[code];
    });
    return returnVal;
};
const isReadonlyMap = ref({});
const isReservation = ref('N');

const initialData = {
    reservation_name: '',
    reservation_phone: '',
    customer_type: 'INDV',
    corporation_name: '',
    corporation_no: '',
    business_no: '',
    corporation_address: '',
    corporation_address_detail: '',
    corporation_manager: '',
    corporation_manager_contact: '',
    driver_name: '',
    driver_phone: '',
    driver_birth_date: '',
    driver_address: '',
    driver_address_detail: '',
    driver_license_no: '',
    driver_license_type: '',
    driver_license_validity: 'N',
    is_second_driver: false,
    second_driver_name: '',
    second_driver_phone: '',
    second_driver_birth_date: '',
    second_driver_address: '',
    second_driver_address_detail: '',
    second_driver_license_no: '',
    second_driver_license_type: '',
    second_driver_license_validity: 'N'
};

const data = reactive({ ...initialData });

const initialValidMessage = {
    reservation_name: '',
    reservation_phone: '',
    customer_type: '',
    corporation_name: '',
    corporation_no: '',
    business_no: '',
    corporation_address: '',
    corporation_manager: '',
    corporation_manager_contact: '',
    driver_name: '',
    driver_phone: '',
    driver_birth_date: '',
    driver_address: '',
    driver_address_detail: '',
    driver_license_no: '',
    driver_license_type: '',
    driver_license_validity: '',
    second_driver_name: '',
    second_driver_phone: '',
    second_driver_birth_date: '',
    second_driver_address: '',
    second_driver_license_no: '',
    second_driver_license_type: '',
    second_driver_license_validity: ''
};
const validationMessage = reactive({ ...initialValidMessage });
const allowedKeys = Object.keys(initialData);
const corpKeys = ['corporation_name', 'corporation_no', 'business_no', 'corporation_address', 'corporation_address_detail', 'corporation_manager', 'corporation_manager_contact'];
const secondDriverKeys = ['second_driver_name', 'second_driver_phone', 'second_driver_birth_date', 'second_driver_address', 'second_driver_address_detail', 'second_driver_license_no', 'second_driver_license_type', 'second_driver_license_validity'];

const requiredData = [];
const allowedSet = new Set(allowedKeys);
const defaultRequired = computed(() => {
    const arr = ['reservation_name', 'reservation_phone', 'customer_type'];
    if (data.customer_type === 'CORP') arr.push('corporation_name');
    return arr;
});

const requiredKeys = computed(() => {
    const res = props.requiredList;

    if (Array.isArray(res) && res.length) {
        return res.filter((k) => allowedSet.has(k));
    }

    const base = new Set(defaultRequired.value);
    if (res && typeof res === 'object' && Object.keys(res).length) {
        for (const [k, v] of Object.entries(res)) {
            if (!allowedSet.has(k)) continue;
            if (v) base.add(k);
            else base.delete(k);
        }
    }
    return Array.from(base);
});

const requiredKeySet = computed(() => new Set(requiredKeys.value));
const isRequired = (key) => requiredKeySet.value.has(key);

const propsReadonlyEnforced = computed(() => {
    const res = props.readonlyList;
    if (Array.isArray(res)) return new Set(res);
    if (res && typeof res === 'object') return new Set(Object.keys(res).filter((key) => !!res[key]));
    return new Set();
});
const propsReadonlyControlled = computed(() => {
    const res = props.readonlyList;
    if (Array.isArray(res)) return new Set(res);
    if (res && typeof res === 'object') return new Set(Object.keys(res));
    return new Set();
});

const allReadonly = ref(false);
const setAllReadonly = (val = true) => {
    allReadonly.value = !!val;
};

const internalReadonlyKeys = computed(() => {
    const set = new Set();
    if (!data.is_second_driver) secondDriverKeys.forEach((key) => set.add(key));
    Object.entries(isReadonlyMap.value).forEach(([key, val]) => {
        if (val) set.add(key);
    });

    if (allReadonly.value) allowedKeys.forEach((k) => set.add(k));
    return set;
});

const finalReadonlySet = computed(() => {
    const result = new Set(propsReadonlyEnforced.value);
    internalReadonlyKeys.value.forEach((key) => {
        if (!propsReadonlyControlled.value.has(key)) result.add(key);
    });
    return result;
});
const isFieldReadonly = (key) => finalReadonlySet.value.has(key);

const propsHideEnforced = computed(() => {
    const res = props.hideList;
    if (Array.isArray(res)) return new Set(res);
    if (res && typeof res === 'object') return new Set(Object.keys(res).filter((k) => !!res[k]));
    return new Set();
});
const propsHideControlled = computed(() => {
    const res = props.hideList;
    if (Array.isArray(res)) return new Set(res);
    if (res && typeof res === 'object') return new Set(Object.keys(res));
    return new Set();
});

const internalHideKeys = computed(() => {
    const set = new Set();
    if (data.customer_type !== 'CORP') corpKeys.forEach((k) => set.add(k));
    return set;
});

const finalHideSet = computed(() => {
    const result = new Set(propsHideEnforced.value);
    internalHideKeys.value.forEach((k) => {
        if (!propsHideControlled.value.has(k)) result.add(k);
    });
    return result;
});
const isHidden = (key) => finalHideSet.value.has(key);

const isEffectivelyRequired = (key) => requiredKeySet.value.has(key) && !isFieldReadonly(key) && !isHidden(key);

const resetData = () => {
    Object.assign(data, initialData);
    Object.assign(validationMessage, initialValidMessage);
};

const getData = () => {
    const payload = {};
    for (const key of allowedKeys) {
        if (!isHidden(key) && !isFieldReadonly(key)) {
            payload[key] = data[key];
        }
    }
    return payload;
};

const setCustomerData = (newVal) => {
    if (!newVal) return;
    const payload = newVal.contract_data ?? newVal;

    Object.entries(payload).forEach(([key, val]) => {
        if (allowedKeys.includes(key)) {
            data[key] = val;
        }
    });
};

const checkRequired = () => {
    Object.keys(validationMessage).forEach((k) => (validationMessage[k] = ''));
    let result = true;

    const keysToCheck = new Set(requiredKeys.value.filter((key) => allowedKeys.includes(key) && isEffectivelyRequired(key)));

    for (const key of keysToCheck) {
        const val = data[key];
        const isEmpty = val === '' || val === null || val === undefined;
        const isIdInvalid = key.endsWith('_id') && (isEmpty || Number(val) === 0);
        if (isEmpty || isIdInvalid) {
            if (key in validationMessage) validationMessage[key] = '필수입력입니다.';
            result = false;
        }
    }
    return result;
};

const setErrorMessage = (payload, reset = false) => {
    if (!payload) return;

    if (reset) {
        Object.keys(validationMessage).forEach((k) => (validationMessage[k] = ''));
    }

    const collectPairs = (node, out = []) => {
        if (node == null) return out;

        if (Array.isArray(node)) {
            node.forEach((item) => collectPairs(item, out));
            return out;
        }

        if (typeof node === 'object') {
            for (const [k, v] of Object.entries(node)) {
                if (v && typeof v === 'object' && !Array.isArray(v)) {
                    collectPairs(v, out);
                } else {
                    out.push([k, v]);
                }
            }
            return out;
        }

        return out;
    };

    const pairs = collectPairs(payload);

    for (const [rawKey, rawVal] of pairs) {
        let key = String(rawKey).replace(/^contract_data\./, '');
        if (!(key in validationMessage)) continue;

        let msg = rawVal;
        if (Array.isArray(msg)) msg = msg.filter(Boolean).join(', ');
        if (msg == null) msg = '';

        validationMessage[key] = String(msg);
    }
};

const searchLicenseChk = (data) => {
    const config = {
        method: 'post'
    };

    return new Promise((resolve, reject) => {
        let callApi = useAxios('/api/driver/license', config, {
            immediate: false,
            onSuccess(response) {
                resolve({
                    success: true,
                    message: 'API 호출에 성공.',
                    details: response
                });
            },
            onError(response) {
                reject({
                    success: false,
                    message: 'API 호출에 실패했습니다.',
                    details: response
                });
            }
        }).execute(data);
        return callApi;
    });
};

const handleLicenseEvent = (number, value) => {
    const isPrimary = number === 1;

    const keyMap = {
        license_no: isPrimary ? 'driver_license_no' : 'second_driver_license_no',
        name: isPrimary ? 'driver_name' : 'second_driver_name',
        type: isPrimary ? 'driver_license_type' : 'second_driver_license_type',
        validity: isPrimary ? 'driver_license_validity' : 'second_driver_license_validity'
    };

    const readonlyKey = keyMap.license_no;

    if (value.event === 'Ninput') {
        const requestData = {
            license_no: data[keyMap.license_no],
            driver_name: data[keyMap.name],
            license_type: data[keyMap.type]
        };

        if (requestData.license_no === '' || requestData.driver_name === '' || requestData.license_type === '') {
            toastComponentRef?.value.showToast('info', '알림', '운전자명, 면허구분, 면허번호를 입력하세요.');
            return;
        }

        searchLicenseChk(requestData)
            .then((res) => {
                const result = res.details?.data;

                if (result?.success) {
                    toastComponentRef?.value.showToast('success', '조회', '면허 진위여부에 성공하였습니다.');
                    data[keyMap.validity] = 'Y';
                    isReadonlyMap.value[readonlyKey] = true;
                } else {
                    toastComponentRef?.value.showToast('error', result?.msg ?? '면허 진위여부 실패');
                    isReadonlyMap.value[readonlyKey] = false;
                }
            })
            .catch((err) => {
                console.log(err);
                toastComponentRef.value.showToast('error', '오류', '면허 진위여부 중 오류가 발생하였습니다.');
                data[keyMap.validity] = 'N';
                isReadonlyMap.value[readonlyKey] = false;
            });
    }

    if (value.event === 'Rinput') {
        data[keyMap.validity] = 'N';
        isReadonlyMap.value[readonlyKey] = false;
    }
};

function handleAddressCompleted(inputId, { address, detailAddress }) {
    data[`${inputId}_address`] = address;
    data[`${inputId}_address_detail`] = detailAddress;
}

function handleDriverCompleted(inputId, { value, birth_date, phone }) {
    if (value === '') return;
    data[`${inputId}_birth_date`] = birth_date;
    data[`${inputId}_phone`] = phone;
}

onMounted(() => {
    //resetData();
    // if (props.customerData && Object.keys(props.customerData).length > 0) {
    //     setcustomerData(props.customerData);
    // }
});

watch(
    () => props.customerData,
    (newVal) => {
        if (!newVal || typeof newVal !== 'object') return;
        setCustomerData(newVal);
    },
    { immediate: true, deep: false }
);

watch(
    () => props.readonlyList,
    () => {
        Object.keys(validationMessage).forEach((key) => {
            if (isFieldReadonly(key)) validationMessage[key] = '';
        });
        // checkRequired();
    },
    { immediate: false, deep: false }
);

watch(
    () => props.hideList,
    () => {
        Object.keys(validationMessage).forEach((key) => {
            if (isHidden(key)) validationMessage[key] = '';
        });
    },
    { immediate: false, deep: false }
);

watch(
    () => props.validMsg,
    (newVal) => {
        if (!newVal || typeof newVal !== 'object') {
            console.error('Invalid Error data');
            return;
        }
        setErrorMessage(newVal, true);
        // Object.keys(newVal).forEach((key) => {
        //     if (key.startsWith('contract_data.')) {
        //         const pureKey = key.replace('contract_data.', '');
        //         if (pureKey in validationMessage) {
        //             validationMessage[pureKey] = newVal[key];
        //         }
        //     } else {
        //         if (key in validationMessage) {
        //             validationMessage[key] = newVal[key];
        //         }
        //     }
        // });
    },
    { immediate: true }
);

watch(
    () => data.customer_type,
    (newVal) => {
        if (newVal === '') {
            nextTick(() => {
                data.customer_type = 'INDV';
                validationMessage.customer_type = '';
            });
        }
    },
    { immediate: true }
);

const isSyncingReservation = ref(false);

watch(isReservation, async (v, oldV) => {
    if (v === 'Y' && oldV !== 'Y') {
        isSyncingReservation.value = true;
        data.driver_name = data.reservation_name;
        data.driver_phone = data.reservation_phone;
        await nextTick();
        isSyncingReservation.value = false;
    }
});

watch([() => data.reservation_name, () => data.reservation_phone, () => data.driver_name, () => data.driver_phone], () => {
    if (isReservation.value === 'Y' && !isSyncingReservation.value) {
        isReservation.value = 'N';
    }
});

defineExpose({
    getData,
    resetData,
    setCustomerData,
    checkRequired, // 필수입력 체크
    setAllReadonly, // 전체 readonly
    setErrorMessage
});
</script>

<template>
    <ToastComponent ref="toastComponentRef" />
    <div class="section-box base-text">
        <div class="section-body">
            <div class="section-left">
                <h2 class="section-title py-4">고객/운전자 정보</h2>
                <div class="section-row">
                    <div class="input-box w-[33%]">
                        <inputbox
                            inputId="reservation_name"
                            v-model="data.reservation_name"
                            :errmsg="validationMessage.reservation_name"
                            :readonly="isFieldReadonly('reservation_name')"
                            :required="isEffectivelyRequired('reservation_name')"
                            label="고객명"
                            :placeholder="'고객명을 입력하세요.'"
                        />
                    </div>
                    <div class="input-box w-[33%]">
                        <inputbox
                            inputId="reservation_phone"
                            v-model="data.reservation_phone"
                            :errmsg="validationMessage.reservation_phone"
                            :readonly="isFieldReadonly('reservation_phone')"
                            :required="isEffectivelyRequired('reservation_phone')"
                            :mask="'phone'"
                            label="고객 전화번호"
                            :placeholder="'숫자만 입력하세요.'"
                        />
                    </div>
                    <div class="input-box w-[33%]">
                        <combobox
                            inputId="customer_type"
                            :options="commonCode('CUSTOMER_TYPE').value"
                            v-model="data.customer_type"
                            :errmsg="validationMessage.customer_type"
                            label="고객구분"
                            :readonly="isFieldReadonly('customer_type')"
                            :required="isEffectivelyRequired('customer_type')"
                        />
                    </div>
                </div>
                <div class="section-row items-center">
                    <div class="input-box w-[33%]">
                        <driver
                            inputId="driver_name"
                            :placeholder="'운전자명을 입력하세요.'"
                            v-model="data.driver_name"
                            :errmsg="validationMessage.driver_name"
                            :readonly="isFieldReadonly('driver_name')"
                            :required="isEffectivelyRequired('driver_name')"
                            @input="(payload) => handleDriverCompleted('driver', payload)"
                            label="운전자명"
                        />
                    </div>
                    <div class="input-box w-[33%]">
                        <inputbox
                            inputId="driver_phone"
                            v-model="data.driver_phone"
                            :errmsg="validationMessage.driver_phone"
                            :readonly="isFieldReadonly('driver_phone')"
                            :required="isEffectivelyRequired('driver_phone')"
                            :mask="'phone'"
                            label="연락처"
                            :placeholder="'연락처를 입력하세요.'"
                        />
                    </div>
                    <div class="input-box w-[33%]">
                        <togglebox inputId="is_reservation" v-model="isReservation" :readonly="isFieldReadonly('driver_name')" spanText="예약자와 동일" label="" />
                    </div>
                </div>
                <div class="section-row items-center">
                    <div class="input-box w-[33%]">
                        <DatePickerWithRegistComponent2
                            inputId="driver_birth_date"
                            v-model="data.driver_birth_date"
                            :required="isEffectivelyRequired('driver_birth_date')"
                            :readonly="isFieldReadonly('driver_birth_date')"
                            :errmsg="validationMessage.driver_birth_date"
                            label="생년월일"
                        />
                    </div>
                    <div class="input-box w-[33%]">
                        <combobox
                            inputId="driver_license_type"
                            v-model="data.driver_license_type"
                            :options="commonCode('LICENSE_TYPE').value"
                            :readonly="isFieldReadonly('driver_license_type')"
                            :required="isEffectivelyRequired('driver_license_type')"
                            :errmsg="validationMessage.driver_license_type"
                            label="면허구분"
                        />
                    </div>
                    <div class="input-box w-[33%]">
                        <licence
                            inputId="driver_license_no"
                            inputId2="driver_license_validity"
                            :placeholder="'면허번호를 입력하세요.'"
                            :button="'검증'"
                            v-model="data.driver_license_no"
                            :modelValue2="data.driver_license_validity"
                            label="면허번호"
                            :readonly="isFieldReadonly('driver_license_no')"
                            @click="(value) => handleLicenseEvent(1, value)"
                        />
                    </div>
                </div>
                <div class="section-row items-center">
                    <div class="input-box w-[50%]">
                        <addresscomponent
                            inputId="driver_address"
                            label="주소"
                            v-model="data.driver_address"
                            :errmsg="validationMessage.driver_address"
                            :required="isEffectivelyRequired('driver_address')"
                            :readonly="isFieldReadonly('driver_address')"
                            @addressCompleted="(payload) => handleAddressCompleted('driver', payload)"
                        />
                    </div>
                    <div class="input-box w-[50%]">
                        <inputbox
                            inputId="driver_address_detail"
                            :placeholder="'상세주소를 입력하세요.'"
                            v-model="data.driver_address_detail"
                            :errmsg="validationMessage.driver_address_detail"
                            :required="isEffectivelyRequired('driver_address_detail')"
                            :readonly="isFieldReadonly('driver_address_detail')"
                        />
                    </div>
                </div>
            </div>
            <div class="section-right">
                <h2 class="section-title py-4">운전자2 정보</h2>
                <div class="section-row items-center">
                    <div class="input-box w-[33%]">
                        <driver
                            inputId="second_driver_name"
                            :placeholder="'제2 운전자명을 입력하세요.'"
                            v-model="data.second_driver_name"
                            :errmsg="validationMessage.second_driver_name"
                            :readonly="isFieldReadonly('second_driver_name')"
                            :required="isEffectivelyRequired('second_driver_name')"
                            @input="(payload) => handleDriverCompleted('second_driver', payload)"
                            label="운전자명"
                        />
                    </div>
                    <div class="input-box w-[33%]">
                        <inputbox
                            inputId="second_driver_phone"
                            v-model="data.second_driver_phone"
                            :errmsg="validationMessage.second_driver_phone"
                            :readonly="isFieldReadonly('second_driver_phone')"
                            :required="isEffectivelyRequired('second_driver_phone')"
                            :mask="'phone'"
                            label="연락처"
                            :placeholder="'연락처를 입력하세요.'"
                        />
                    </div>
                    <div class="input-box w-[33%]">
                        <togglebox inputId="is_second_driver" v-model="data.is_second_driver" :readonly="isFieldReadonly('driver_name')" :isReturnBoolean="true" spanText="제2운전자가 있음" label="" />
                    </div>
                </div>
                <div class="section-row items-center">
                    <div class="input-box w-[33%]">
                        <DatePickerWithRegistComponent2
                            inputId="second_driver_birth_date"
                            v-model="data.second_driver_birth_date"
                            :required="isEffectivelyRequired('second_driver_birth_date')"
                            :readonly="isFieldReadonly('second_driver_birth_date')"
                            :errmsg="validationMessage.second_driver_birth_date"
                            label="생년월일"
                        />
                    </div>
                    <div class="input-box w-[33%]">
                        <combobox
                            inputId="second_driver_license_type"
                            v-model="data.second_driver_license_type"
                            :options="commonCode('LICENSE_TYPE').value"
                            :readonly="isFieldReadonly('second_driver_license_type')"
                            :required="isEffectivelyRequired('second_driver_license_type')"
                            :errmsg="validationMessage.second_driver_license_type"
                            label="면허구분"
                        />
                    </div>
                    <div class="input-box w-[33%]">
                        <licence
                            inputId="second_driver_license_no"
                            inputId2="second_driver_license_validity"
                            :placeholder="'면허번호를 입력하세요.'"
                            :button="'검증'"
                            v-model="data.second_driver_license_no"
                            :modelValue2="data.second_driver_license_validity"
                            label="면허번호"
                            :readonly="isFieldReadonly('second_driver_license_no')"
                            @click="(value) => handleLicenseEvent(2, value)"
                        />
                    </div>
                </div>
                <div class="section-row items-center">
                    <div class="input-box w-[50%]">
                        <addresscomponent
                            inputId="second_driver_address"
                            label="주소"
                            v-model="data.second_driver_address"
                            :errmsg="validationMessage.second_driver_address"
                            :required="isEffectivelyRequired('second_driver_address')"
                            :readonly="isFieldReadonly('second_driver_address')"
                            @addressCompleted="(payload) => handleAddressCompleted('second_driver', payload)"
                        />
                    </div>
                    <div class="input-box w-[50%]">
                        <inputbox
                            inputId="second_driver_address_detail"
                            :placeholder="'상세주소를 입력하세요.'"
                            v-model="data.second_driver_address_detail"
                            :errmsg="validationMessage.second_driver_address_detail"
                            :required="isEffectivelyRequired('second_driver_address_detail')"
                            :readonly="isFieldReadonly('second_driver_address_detail')"
                        />
                    </div>
                </div>
                <div class="text-[0.9rem] mt-3 flex gap-3 bg-[#f5f5f5] border border-[#d1d5db] rounded-[0.4rem] p-3 select-none">
                    <i class="pi pi-check-circle mt-[0.15rem]"></i>
                    <p>
                        국토부 운전자격검증은 RIMS 연동 사업자와 연결된 차량일 때 <br />
                        계약서 작성완료 시점에 자동으로 진행됩니다.<br /><br />
                        - 검증 필수값 : <b>운전자명, 면허번호, 면허구분​ </b><br />
                        - 진위조회는 고객의 면허 상태 조회용입니다.
                    </p>
                </div>
            </div>
        </div>
        <h2 v-if="data.customer_type === 'CORP'" class="section-title mt-7 mb-4">법인 정보</h2>
        <div v-if="data.customer_type === 'CORP'" class="section-body">
            <div class="section-left">
                <div class="section-row">
                    <div class="input-box w-[33%]">
                        <inputbox
                            inputId="corporation_name"
                            v-model="data.corporation_name"
                            :errmsg="validationMessage.corporation_name"
                            :readonly="isFieldReadonly('corporation_name')"
                            :required="isEffectivelyRequired('corporation_name')"
                            label="법인명"
                            :placeholder="'법인명을 입력하세요.'"
                        />
                    </div>
                    <div class="input-box w-[33%]">
                        <inputbox
                            inputId="business_no"
                            v-model="data.business_no"
                            :errmsg="validationMessage.business_no"
                            :readonly="isFieldReadonly('business_no')"
                            :required="isEffectivelyRequired('business_no')"
                            :mask="'business'"
                            label="사업자번호"
                            :placeholder="'사업자번호를 입력하세요.'"
                        />
                    </div>
                    <div class="input-box w-[33%]">
                        <inputbox
                            inputId="corporation_no"
                            v-model="data.corporation_no"
                            :errmsg="validationMessage.corporation_no"
                            :readonly="isFieldReadonly('corporation_no')"
                            :required="isEffectivelyRequired('corporation_no')"
                            :mask="'resident'"
                            label="법인번호"
                            :placeholder="'법인번호를 입력하세요.'"
                        />
                    </div>
                </div>
                <div class="section-row items-center">
                    <div class="input-box w-[50%]">
                        <addresscomponent
                            inputId="corporation_address"
                            label="주소"
                            v-model="data.corporation_address"
                            :errmsg="validationMessage.corporation_address"
                            :required="isEffectivelyRequired('corporation_address')"
                            :readonly="isFieldReadonly('corporation_address')"
                            @addressCompleted="(payload) => handleAddressCompleted('corporation', payload)"
                        />
                    </div>
                    <div class="input-box w-[50%]">
                        <inputbox
                            inputId="corporation_address_detail"
                            :placeholder="'상세주소를 입력하세요.'"
                            v-model="data.corporation_address_detail"
                            :errmsg="validationMessage.corporation_address_detail"
                            :required="isEffectivelyRequired('corporation_address_detail')"
                            :readonly="isFieldReadonly('corporation_address_detail')"
                        />
                    </div>
                </div>
            </div>

            <div class="section-right">
                <div class="section-row items-center">
                    <div class="input-box w-[50%]">
                        <inputbox
                            inputId="corporation_manager"
                            v-model="data.corporation_manager"
                            :errmsg="validationMessage.corporation_manager"
                            :readonly="isFieldReadonly('corporation_manager')"
                            :required="isEffectivelyRequired('corporation_manager')"
                            label="담당자명"
                            :placeholder="'담당자명을 입력하세요.'"
                        />
                    </div>
                    <div class="input-box w-[50%]">
                        <inputbox
                            inputId="corporation_manager_contact"
                            v-model="data.corporation_manager_contact"
                            :errmsg="validationMessage.corporation_manager_contact"
                            :readonly="isFieldReadonly('corporation_manager_contact')"
                            :required="isEffectivelyRequired('corporation_manager_contact')"
                            :mask="'phone'"
                            label="담당자 연락처"
                            :placeholder="'담당자 연락처를 입력하세요.'"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<style src="@/assets/css/form-common.css"></style>
